/**
 * useCentralCreditBalance — the workspace's spendable credit balance, read from
 * Central (GET /api/credits/balance, field `available`; suite contract §1).
 *
 *   balance  a number only when Central stated one; null = unknown (not loaded,
 *            unreadable, no session) — shown "Unavailable", never 0
 *   reload   re-read on demand (Retry)
 *
 * Re-reads when the token changes (sign-in, renewal, workspace switch — the
 * balance is per workspace) and on every announced billing refresh
 * (`?billing=` return / focus after Central billing was opened; see
 * BootReadyEffects). BeHeard never computes, grants or caches credits itself.
 *
 * Safe outside a Redux <Provider> (same rule as useCentralBillingLinks): with
 * no store there is no token, so the balance is unknown.
 */

import { useCallback, useContext, useEffect, useReducer, useState, useSyncExternalStore } from 'react';
import { ReduxContext } from './useCentralBillingLinks';
import { getWalletBalance } from '../services/centralBillingApi';
import { onCentralBillingRefresh } from '../services/centralBillingUrls';

export interface CentralCreditBalance {
  balance: number | null;
  isLoading: boolean;
  reload: () => void;
}

/** "1,250" for a stated balance; "Unavailable" when Central did not state one. */
export function creditBalanceLabel(balance: number | null): string {
  return balance === null ? 'Unavailable' : balance.toLocaleString('en-US');
}

const noopSubscribe = () => () => undefined;

export function useCentralCreditBalance(): CentralCreditBalance {
  const store = useContext(ReduxContext)?.store ?? null;
  const subscribe = useCallback(
    (cb: () => void) => (store ? store.subscribe(cb) : noopSubscribe()),
    [store],
  );
  const getToken = () =>
    store
      ? ((store.getState() as { centralAuth?: { accessToken?: string | null } })?.centralAuth?.accessToken ?? null)
      : null;
  const token = useSyncExternalStore(subscribe, getToken, getToken);

  const [balance, setBalance] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(token));
  const [reloadKey, reload] = useReducer((n: number) => n + 1, 0);

  useEffect(() => onCentralBillingRefresh(reload), [reload]);

  useEffect(() => {
    if (!token) {
      setBalance(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    void getWalletBalance(token).then((value) => {
      if (cancelled) return;
      setBalance(value);
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [token, reloadKey]);

  return { balance, isLoading, reload };
}
